<template>
    <!-- 2. Input/Output Topics for SQL transforms -->
    <Card class="transition-opacity">
        <CardHeader>
            <CardTitle class="text-xl"
                ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">2</Badge>Input / Output Topics</CardTitle
            >
            <CardDescription>
                Define patterns and output topics via the
                <a href="/topics" target="_blank" class="text-accent-foreground underline">topics page</a>.
            </CardDescription>
        </CardHeader>
        <CardContent>
            <!-- SQL Join specific inputs -->
            <div v-if="isSqlJoinType" class="space-y-6">
                <div>
                    <h3 class="text-lg font-semibold mb-4">Input Tables Topic Patterns</h3>
                    <div class="space-y-4">
                        <div v-for="(_, index) in inputPatterns" :key="index" class="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
                            <div>
                                <Label :for="`input-pattern-${index}`" class="mb-2 block text-sm font-medium"
                                    >Table {{ index + 1 }} Topic Pattern</Label
                                >
                                <Input
                                    :id="`input-pattern-${index}`"
                                    v-model="inputPatterns[index]"
                                    @input="updateInputPattern(index, $event.target.value)"
                                    :disabled="loadingSetInOutTopics"
                                    type="text"
                                    class="w-full"
                                    :placeholder="`e.g. .*\\.database\\.table-${index + 1}-prefix.*`" />
                            </div>
                            <Button
                                v-if="inputPatterns.length > 2 && index >= 2"
                                variant="outline"
                                size="sm"
                                class="shrink-0"
                                @click="removeInputPattern(index)"
                                :disabled="loadingSetInOutTopics">
                                <RotateCcw v-if="loadingSetInOutTopics" class="h-4 w-4 animate-spin" />
                                <span v-else>Remove</span>
                            </Button>
                        </div>
                        <Button
                            variant="secondary"
                            size="sm"
                            class="gap-2"
                            @click="addInputPattern"
                            :disabled="loadingSetInOutTopics || inputPatterns.length >= 5">
                            <RotateCcw v-if="loadingSetInOutTopics" class="h-4 w-4 animate-spin" />
                            <span v-else>Add Input Table</span>
                        </Button>
                    </div>
                </div>

                <!-- Output Topic Configuration -->
                <div class="border-t pt-6">
                    <h3 class="text-lg font-semibold mb-4">Output Topic Configuration</h3>
                    <div class="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
                        <div>
                            <div class="flex items-center gap-2 mb-2">
                                <Label for="join-output-topic" class="text-sm font-medium">Output Topic Replacement</Label>
                                <Tooltip>
                                    <TooltipTrigger as-child>
                                        <Button variant="ghost" size="icon" class="h-4 w-4 p-0">
                                            <Info class="h-3 w-3 text-muted-foreground" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Backreferences supported (e.g., $1, $2 for regex capture groups)</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <Input
                                id="join-output-topic"
                                v-model="outputTopic"
                                :disabled="loadingSetInOutTopics"
                                :class="{ 'border-destructive focus-visible:ring-red-500': setInOutError }"
                                type="text"
                                class="w-full"
                                placeholder="e.g. joined.output_table" />
                        </div>
                        <LoadingButton class="bg-primary hover:bg-primary shrink-0" @click="callSetInOutTopics" :is-loading="loadingSetInOutTopics">
                            <Settings class="w-4 h-4 mr-2" />
                            Save Topics Pattern / Replacement
                        </LoadingButton>
                    </div>

                    <Alert v-if="setInOutResult" class="mt-4">
                        <CheckCircle class="h-4 w-4" />
                        <AlertDescription>{{ setInOutResult }}</AlertDescription>
                    </Alert>
                    <div v-if="setInOutError" class="mt-2">
                        <ErrorMessage :error="setInOutError" :to-sentry="true" context="transform-impl-set-topics" />
                    </div>
                </div>

                <!-- Input/Output Topics Table -->
                <div class="border-t pt-6">
                    <div v-if="fetchInOutError" class="mb-4">
                        <ErrorMessage :error="fetchInOutError" :to-sentry="true" context="transform-impl-fetch-topics" />
                    </div>
                    <div class="table-page-container mb-6">
                        <div class="table-page-body">
                            <div>
                                <ScrollArea
                                    class="w-full border shadow-md rounded-md"
                                    :class="(inOutTopics.length || 0) <= 10 ? 'h-auto' : 'h-40-dynamic'">
                                    <Table>
                                        <TableHeader class="sticky top-0 bg-background z-10">
                                            <TableRow class="bg-muted/50">
                                                <TableHead class="first:rounded-tl-md">Input Topic</TableHead>
                                                <TableHead class="last:rounded-tr-md">Expected Output Topic Name</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            <!-- Skeleton rows when loading -->
                                            <template v-if="topicTableDataLoading">
                                                <TableRow v-for="n in 5" :key="`skeleton-${n}`">
                                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                                </TableRow>
                                            </template>
                                            <!-- Actual data rows -->
                                            <template v-else>
                                                <TableRow v-for="inOutTopic in inOutTopics" :key="inOutTopic[0]">
                                                    <TableCell class="font-medium">
                                                        <div class="flex flex-col items-start gap-1">
                                                            <div class="whitespace-normal break-words">
                                                                <Button
                                                                    variant="link"
                                                                    class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                                    @click="() => navigateToTopic(inOutTopic[0])">
                                                                    <span class="hover:text-primary truncate">
                                                                        {{ inOutTopic[0].replace(/(source_|transform_)[^.]+\./, '') }}
                                                                    </span>
                                                                </Button>
                                                            </div>
                                                            <small class="text-muted-foreground">ID: {{ inOutTopic[0] }}</small>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell class="font-medium">
                                                        <div class="flex flex-col items-start gap-1">
                                                            <div class="whitespace-normal break-words">
                                                                {{ inOutTopic[1].replace(/(source_|transform_)[^.]+\./, '') }}
                                                            </div>
                                                            <small class="text-muted-foreground">ID: {{ inOutTopic[1] }}</small>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                                <TableEmpty v-if="!topicTableDataLoading && !inOutTopics.length" :colspan="2">
                                                    <div class="flex flex-col items-center justify-center py-6">
                                                        <p class="text-muted-foreground text-sm">No topics found for the given pattern.</p>
                                                    </div>
                                                </TableEmpty>
                                            </template>
                                        </TableBody>
                                    </Table>
                                </ScrollArea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Enrich specific inputs -->
            <div v-if="isEnrichType" class="space-y-6">
                <div>
                    <h3 class="text-lg font-semibold mb-4">Input Tables Topic Patterns</h3>
                    <div class="space-y-4">
                        <div>
                            <Label for="enrich-main-pattern" class="mb-2 block text-sm font-medium"
                                >Main Table Pattern <span class="text-muted-foreground">(stream to be enriched)</span></Label
                            >
                            <Input
                                id="enrich-main-pattern"
                                v-model="patternMainTable"
                                :disabled="loadingSetInOutTopics"
                                type="text"
                                class="w-full"
                                placeholder="e.g. .*\\.database\\.main-table-prefix.*" />
                        </div>
                        <div>
                            <Label for="enrich-lookup-pattern" class="mb-2 block text-sm font-medium"
                                >Lookup Table Pattern <span class="text-muted-foreground">(containing enrichment data)</span></Label
                            >
                            <Input
                                id="enrich-lookup-pattern"
                                v-model="patternLookupTable"
                                :disabled="loadingSetInOutTopics"
                                type="text"
                                class="w-full"
                                placeholder="e.g. .*\\.database\\.lookup-table-prefix.*" />
                        </div>
                    </div>
                </div>

                <!-- Step 2.2 - Output Topic Configuration -->
                <div class="border-t pt-6">
                    <h3 class="text-lg font-semibold mb-4">Output Topic Configuration</h3>
                    <div class="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
                        <div>
                            <div class="flex items-center gap-2 mb-2">
                                <Label for="enrich-output-topic" class="text-sm font-medium">Output Topic Replacement</Label>
                                <Tooltip>
                                    <TooltipTrigger as-child>
                                        <Button variant="ghost" size="icon" class="h-4 w-4 p-0">
                                            <Info class="h-3 w-3 text-muted-foreground" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Backreferences supported (e.g., $1, $2 for regex capture groups)</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <Input
                                id="enrich-output-topic"
                                v-model="outputTopic"
                                :disabled="loadingSetInOutTopics"
                                :class="{ 'border-destructive focus-visible:ring-red-500': setInOutError }"
                                type="text"
                                class="w-full"
                                placeholder="e.g. enriched.output_table" />
                        </div>
                        <LoadingButton class="bg-primary hover:bg-primary shrink-0" @click="callSetInOutTopics" :is-loading="loadingSetInOutTopics">
                            <Settings class="w-4 h-4 mr-2" />
                            Save Topics Pattern / Replacement
                        </LoadingButton>
                    </div>

                    <Alert v-if="setInOutResult" class="mt-4">
                        <CheckCircle class="h-4 w-4" />
                        <AlertDescription>{{ setInOutResult }}</AlertDescription>
                    </Alert>
                    <div v-if="setInOutError" class="mt-2">
                        <ErrorMessage :error="setInOutError" :to-sentry="true" context="transform-impl-set-topics" />
                    </div>
                </div>

                <!-- Input/Output Topics Table -->
                <div class="border-t pt-6">
                    <div v-if="fetchInOutError" class="mb-4">
                        <ErrorMessage :error="fetchInOutError" :to-sentry="true" context="transform-impl-fetch-topics" />
                    </div>
                    <div class="table-page-container mb-6">
                        <div class="table-page-body">
                            <div>
                                <ScrollArea
                                    class="w-full border shadow-md rounded-md"
                                    :class="(inOutTopics.length || 0) <= 10 ? 'h-auto' : 'h-40-dynamic'">
                                    <Table>
                                        <TableHeader class="sticky top-0 bg-background z-10">
                                            <TableRow class="bg-muted/50">
                                                <TableHead class="first:rounded-tl-md">Input Topic</TableHead>
                                                <TableHead class="last:rounded-tr-md">Expected Output Topic Name</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            <!-- Skeleton rows when loading -->
                                            <template v-if="topicTableDataLoading">
                                                <TableRow v-for="n in 5" :key="`skeleton-${n}`">
                                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                                </TableRow>
                                            </template>
                                            <!-- Actual data rows -->
                                            <template v-else>
                                                <TableRow v-for="inOutTopic in inOutTopics" :key="inOutTopic[0]">
                                                    <TableCell class="font-medium">
                                                        <div class="flex flex-col items-start gap-1">
                                                            <div class="whitespace-normal break-words">
                                                                <Button
                                                                    variant="link"
                                                                    class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                                    @click="() => navigateToTopic(inOutTopic[0])">
                                                                    <span class="hover:text-primary truncate">
                                                                        {{ inOutTopic[0].replace(/(source_|transform_)[^.]+\./, '') }}
                                                                    </span>
                                                                </Button>
                                                            </div>
                                                            <small class="text-muted-foreground">ID: {{ inOutTopic[0] }}</small>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell class="font-medium">
                                                        <div class="flex flex-col items-start gap-1">
                                                            <div class="whitespace-normal break-words">
                                                                {{ inOutTopic[1].replace(/(source_|transform_)[^.]+\./, '') }}
                                                            </div>
                                                            <small class="text-muted-foreground">ID: {{ inOutTopic[1] }}</small>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                                <TableEmpty v-if="!topicTableDataLoading && !inOutTopics.length" :colspan="2">
                                                    <div class="flex flex-col items-center justify-center py-6">
                                                        <p class="text-muted-foreground text-sm">No topics found for the given pattern.</p>
                                                    </div>
                                                </TableEmpty>
                                            </template>
                                        </TableBody>
                                    </Table>
                                </ScrollArea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Standard transform (non-SQL) -->
            <div v-if="!isJoinOrEnrichType" class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-4 w-full">
                <div>
                    <Label for="standard-input-pattern" class="mb-2 block">Input Topic Pattern</Label>
                    <Input
                        id="standard-input-pattern"
                        v-model="pattern"
                        :disabled="loadingSetInOutTopics"
                        :class="{ 'border-destructive focus-visible:ring-red-500': setInOutError || fetchInOutError }"
                        type="text"
                        placeholder="e.g. .*\\.test\\.some-topic-prefix.*" />
                </div>
                <div>
                    <div class="flex items-center gap-2 mb-2">
                        <Label for="standard-output-topic" class="block">Output Topic Replacement</Label>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="ghost" size="icon" class="h-4 w-4 p-0">
                                    <Info class="h-3 w-3 text-muted-foreground" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Backreferences supported (e.g., $1, $2 for regex capture groups)</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    <Input
                        id="standard-output-topic"
                        v-model="outputTopic"
                        :disabled="loadingSetInOutTopics"
                        :class="{ 'border-destructive focus-visible:ring-red-500': setInOutError }"
                        type="text"
                        placeholder="e.g. test.merged_orders" />
                </div>
                <div class="flex items-end">
                    <LoadingButton class="bg-primary hover:bg-primary" @click="callSetInOutTopics" :is-loading="loadingSetInOutTopics">
                        <Settings class="w-4 h-4 mr-2" />
                        Save Topics Pattern / Replacement
                    </LoadingButton>
                </div>
            </div>
        </CardContent>
    </Card>

    <!-- 3. Transform Logic -->
    <Card v-if="isJoinOrEnrichType" class="transition-opacity">
        <CardHeader>
            <CardTitle class="text-xl"
                ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">3</Badge>Transform Logic</CardTitle
            >
            <CardDescription>Define SQL schema and transformation logic</CardDescription>
        </CardHeader>
        <CardContent>
            <!-- Display validation errors -->
            <div v-if="saveError !== null" class="mb-4">
                <ErrorMessage
                    :error="saveError"
                    :to-sentry="true"
                    context="transform-sql-save"
                    introduction="Failed to save transform implementation" />
            </div>

            <!-- Monaco Tabbed Editor -->
            <MonacoTabbedEditor
                v-if="editorTabs.length > 0"
                :tabs="editorTabs"
                :loading="editorLoading"
                :is-saving="isSaving"
                :template-map="templateMap"
                language="sql"
                @save="handleTabSave"
                @content-change="handleContentChangeWithAutoSave"
                @load-template="handleLoadTemplate"
                @tab-change="handleTabChange"
                @close="closeEditor" />
        </CardContent>
    </Card>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { debounce } from 'lodash';
import { useTransformEditor } from '@/composables/useTransformEditor';
import { MonacoTabbedEditor } from '@/components/ui/monaco-tabbed-editor';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Settings, RotateCcw, CheckCircle, Info } from 'lucide-vue-next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import ErrorMessage from '@/components/ErrorMessage.vue';
import { TRANSFORM_TYPE_ENUMS } from '@/utils/constants';
import { useTopicPatternManager } from '@/composables/useTopicPatternManager';

// Import shared utility for tab creation
import { createSqlTransformTabs } from '@/utils/transform-tabs';

// Import SQL template files
import createTableMainTemplate from '../transform_code/createTableMain.sql?raw';
import createTableLookupTemplate from '../transform_code/createTableLookup.sql?raw';
import enrichSqlTemplate from '../transform_code/enrichSql.sql?raw';
import createTable1Template from '../transform_code/createTable1.sql?raw';
import createTable2Template from '../transform_code/createTable2.sql?raw';
import sqlJoinTemplate from '../transform_code/sqlJoin.sql?raw';

// Props - SIMPLIFIED: Pure UI component receives data only
const props = defineProps({
    transform: {
        type: Object,
        required: true
    },
    implementationCurrentVersion: {
        type: Object,
        required: true
    },
    hasUnsavedChanges: {
        type: Boolean,
        default: false
    },
    isSaving: {
        type: Boolean,
        default: false
    },
    saveError: {
        type: Object,
        default: null
    }
});

// Emits - SIMPLIFIED: Only emit content changes and navigation
const emit = defineEmits(['implementation-updated', 'implementation-synced', 'show-example-clicked']);

// Use shared transform editor functionality
const templateMap = {
    CREATE_TABLE_MAIN: createTableMainTemplate,
    CREATE_TABLE_LOOKUP: createTableLookupTemplate,
    ENRICH_SQL: enrichSqlTemplate,
    CREATE_TABLE_1: createTable1Template,
    CREATE_TABLE_2: createTable2Template,
    CREATE_TABLE_3: createTable1Template, // Reuse template 1 for table 3+
    CREATE_TABLE_4: createTable1Template,
    CREATE_TABLE_5: createTable1Template,
    JOIN_SQL: sqlJoinTemplate
};

const {
    monacoTheme,
    activeTabId,
    isSaving,
    handleTabSave,
    handleContentChange,
    handleLoadTemplate,
    handleTabChange,
    closeEditor,
    initializeActiveTab
} = useTransformEditor(props, emit, {
    defaultTabId: 'ENRICH_SQL', // Will be overridden in initializeSqlTransformData
    templateMap
});

// Computed properties for transform type detection
const isSqlJoinType = computed(() => props.transform?.transform_type === TRANSFORM_TYPE_ENUMS.sql_join);
const isEnrichType = computed(() => props.transform?.transform_type === TRANSFORM_TYPE_ENUMS.enrich);
const isJoinOrEnrichType = computed(() => isSqlJoinType.value || isEnrichType.value);

// SQL-specific state (needs to be defined before composable)
const inputPatterns = ref(['', '']); // Start with 2 empty patterns for SQL joins
const patternMainTable = ref('');
const patternLookupTable = ref('');

const {
    pattern,
    outputTopic,
    inOutTopics,
    loadingSetInOutTopics,
    topicTableDataLoading,
    setInOutError,
    setInOutResult,
    fetchInOutError,
    callSetInOutTopics,
    fetchInOutTopics,
    initializePatternData,
    navigateToTopic,
    debouncedPatternSave,
    createContentChangeHandler
} = useTopicPatternManager(props, emit, {
    inputPatterns,
    patternMainTable,
    patternLookupTable
});

// Create auto-save enabled content change handler - all logic is now internal to composable
const handleContentChangeWithAutoSave = createContentChangeHandler(
    handleContentChange,
    (tabId) => tabId.startsWith('CREATE_TABLE') || tabId === 'ENRICH_SQL' || tabId === 'JOIN_SQL'
);

// Local state - Editor (SIMPLIFIED: UI state only)
const editorLoading = ref(false);

// Input pattern management for SQL joins
const addInputPattern = () => {
    inputPatterns.value.push('');

    // Also update the implementation inputTables (matching old implementation)
    if (props.implementationCurrentVersion?.implementation?.inputTables) {
        props.implementationCurrentVersion.implementation.inputTables.push({
            topicMatcherRegex: '',
            createTableSQL: 'TBD',
            name: `input_table_${inputPatterns.value.length}`
        });
    }
};

const removeInputPattern = (index) => {
    if (inputPatterns.value.length > 2) {
        inputPatterns.value.splice(index, 1);

        // Also remove from implementation inputTables (matching old implementation)
        if (props.implementationCurrentVersion?.implementation?.inputTables) {
            props.implementationCurrentVersion.implementation.inputTables.splice(index, 1);
        }
    }
};

const updateInputPattern = (index, value) => {
    if (inputPatterns.value[index] !== undefined) {
        inputPatterns.value[index] = value;

        // Also update the implementation inputTables (matching old implementation)
        if (props.implementationCurrentVersion?.implementation?.inputTables?.[index]) {
            props.implementationCurrentVersion.implementation.inputTables[index].topicMatcherRegex = value;
        }

        computePatternForJoin();
    }
};

// Pattern computation
const computePatternForJoin = () => {
    if (isSqlJoinType.value) {
        const validPatterns = inputPatterns.value.filter((p) => p.trim() !== '');
        if (validPatterns.length > 0) {
            pattern.value = `(${validPatterns.join('|')})`;
        }
    }
};

const computePatternForEnrich = () => {
    if (isEnrichType.value) {
        const main = patternMainTable.value.trim();
        const lookup = patternLookupTable.value.trim();
        if (main && lookup) {
            pattern.value = `(${main}|${lookup})`;
        }
    }
};

watch(
    () => props.implementationCurrentVersion?.implementation?.inputTables,
    (newInputTables) => {
        if (newInputTables && isSqlJoinType.value) {
            inputPatterns.value = newInputTables.map((table) => table.topicMatcherRegex || '');
        }
    },
    { immediate: true }
);

// Watch for enrich tables changes
watch(
    () => [props.implementationCurrentVersion?.implementation?.mainTable, props.implementationCurrentVersion?.implementation?.lookupTable],
    ([newMainTable, newLookupTable]) => {
        if (isEnrichType.value) {
            if (newMainTable) patternMainTable.value = newMainTable.topicMatcherRegex || '';
            if (newLookupTable) patternLookupTable.value = newLookupTable.topicMatcherRegex || '';
        }
    },
    { immediate: true, deep: true }
);

// Watch for user input changes to enrich pattern fields to trigger unified auto-save
watch([patternMainTable, patternLookupTable], () => {
    if (isEnrichType.value) {
        computePatternForEnrich();
        // Use unified auto-save system from useTopicPatternManager
        debouncedPatternSave();
    }
});

// Computed tabs for MonacoTabbedEditor
const editorTabs = computed(() => {
    const impl = props.implementationCurrentVersion?.implementation;
    const transform_type = props.transform?.transform_type;

    if (transform_type === TRANSFORM_TYPE_ENUMS.sql_join) {
        // For join transforms, pass the table count based on inputPatterns length
        const tableCount = Math.max(2, inputPatterns.value.length);
        return createSqlTransformTabs(impl, 'sql_join', tableCount);
    } else if (transform_type === TRANSFORM_TYPE_ENUMS.enrich) {
        return createSqlTransformTabs(impl, 'enrich');
    }

    return [];
});

// Initialize data from props
const initializeSqlTransformData = () => {
    if (props.transform && props.implementationCurrentVersion) {
        initializePatternData();

        // SQL-specific initialization (matching old implementation)
        if (isSqlJoinType.value) {
            // For SQL joins, initialize input patterns from implementation inputTables
            const inputTables = props.implementationCurrentVersion.implementation?.inputTables || [];
            if (inputTables.length >= 2) {
                inputPatterns.value = inputTables.map((table) => table.topicMatcherRegex || '');
            } else {
                // Default to 2 empty patterns if no inputTables exist
                inputPatterns.value = ['', ''];
            }
        } else if (isEnrichType.value) {
            // For enrich, initialize main and lookup patterns from implementation
            patternMainTable.value = props.implementationCurrentVersion.implementation?.mainTable?.topicMatcherRegex || '';
            patternLookupTable.value = props.implementationCurrentVersion.implementation?.lookupTable?.topicMatcherRegex || '';
        }

        // Set default active tab based on transform type - wait for next tick for editorTabs to be computed
        const defaultTabId = isSqlJoinType.value ? 'JOIN_SQL' : 'ENRICH_SQL';
        // Use nextTick to ensure editorTabs is computed before initializing
        nextTick(() => {
            initializeActiveTab(editorTabs.value, defaultTabId);
        });
    }
};

watch(
    () => props.transform,
    () => {
        initializeSqlTransformData();
    },
    { immediate: true, deep: true }
);
</script>
